import { ChangeDetectionStrategy, Component, ElementRef, ViewChild } from '@angular/core';
import { EventsService } from '@shared/services';
import tinymce, { Editor, EditorEvent } from 'tinymce';
import { BaseFrameEditorComponent } from '../base-frame-editor/base-frame-editor.component';
import { FrameEditorConstants } from '../../constants/frame-editor.constants';
import { FrameEditorMediaService } from '../../services/frame-editor-media-service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { isKey, keyCodes } from '@local/ts-infra';
import { EditorComponent } from '@tinymce/tinymce-angular';

@UntilDestroy()
@Component({
  selector: 'frame-editor',
  templateUrl: './frame-editor.component.html',
  styleUrls: ['../base-frame-editor/base-frame-editor.scss', './frame-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FrameEditorComponent extends BaseFrameEditorComponent {
  @ViewChild('editor') editor: EditorComponent;
  @ViewChild('editorContainer') editorContainer: ElementRef;
  protected readonlyFrame = false;

  constructor(
    private eventsService: EventsService,
    private frameEditorMediaService: FrameEditorMediaService
  ) {
    super();
    this.frameEditorContextMenuService.destroyHandlers();
  }

  setUpEditor() {
    this.frameEditorService.initTinyMce(this.followingId, this.id, this.sendCommandTelemetryEvent.bind(this));
    this.updateEditorMode();
    super.setUpEditor();
  }

  updateEditorMode() {
    setTimeout(() => {
      if (this.editor?.editor) {
        this.editor.editor.save();
      }
    }, 0);
  }

  setEditorInit(): any {
    return {
      ...this.frameEditorService.getEditorInit(),
      placeholder: "Type '/' for commands...",
      plugins: FrameEditorConstants.PLUGINS,
      body_class: `${this.bodyClass} edit-mode`,
      object_resizing: true,
      toolbar: FrameEditorConstants.TOOLBAR,
      quickbars_selection_toolbar: FrameEditorConstants.QUICKBARS_SELECTION_TOOLBAR,
      contextmenu: FrameEditorConstants.CONTEXTMENU,
      file_picker_callback: (callback, value, meta) =>
        this.frameEditorMediaService.addImageOrVideoToEditor(callback, meta, this.followingId, this.id),
      setup: (editor: Editor) => {
        this.setSetupEditorInit(editor);
      },
    };
  }

  setSetupEditorInit(editor: Editor) {
    this.frameEditorService.setupTinyMce(editor, this.followingId, this.id);
    this.addKeyboardToEditor(editor);
    this.addLinkListenersToEditor(editor);
    editor.on('init', () => {
      editor.contentDocument.addEventListener('click', (e) => {
        if (this.frameEditorContextMenuService?.renameAttachmentPopupOpen) {
          this.frameEditorContextMenuService?.renameAttachmentPopupOpen.destroy();
        }
        this.frameEditorService.addNewLineToEnd(e, this.id, editor);
      });
      this.handleInitEditor(editor);
    });
    editor.on('paste', (event) => {
      this.frameEditorService.editorOnPasteEvent(event, editor, this.followingId, this.id);
    });
    editor.on('drop', (event) => {
      this.frameEditorService.editorOnDropEvent(event, editor, this.followingId, this.id);
    });
    editor.on('focusin', () => {
      this.changeFocus('frame-editor-focus');
    });
  }

  onInitEditor() {
    this.updateEditorMode();
    this.onInit.emit();
  }

  handleKeys(event: EditorEvent<KeyboardEvent>) {
    if (isKey(event, keyCodes.backspace)) {
      const selectedNode = tinymce.get(this.id).selection.getNode();
      if (
        selectedNode.textContent === '' &&
        selectedNode.firstChild?.nodeName === 'BR' &&
        selectedNode.previousSibling?.nodeName === 'TABLE'
      ) {
        selectedNode.previousSibling.remove();
        tinymce.get(this.id).save();
      }
    }
  }

  private sendCommandTelemetryEvent(actionName: string) {
    this.eventsService.event('collections.card_commands_menu', {
      label: actionName,
      location: {
        title: this.telemetryLocation,
      },
    });
  }
}
